import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/utils/telemetry';
import api from '@/utils/api';
import { getSignupFunnelSessionId } from '@/utils/signupFunnel';

/**
 * TrackedCTA — drop-in A/B-tested call-to-action block.
 *
 * Wraps any single-CTA UI element (typically an empty-state card or hero
 * banner) in the same A/B engine that powers the daily splash:
 *   * Sticky per-user variant assignment (FNV-1a hash of user_id|surface|cta_id)
 *   * DB overrides for both A (promoted baseline) and B (current B-variant)
 *     fetched once on mount from `/api/splash/cards/active?surface=<surface>`
 *   * Telemetry events scoped to this surface's event types so the existing
 *     conversion endpoint, Wilson-CI overlay, auto-stage cron, and Monday
 *     digest all "just work" for the new surface.
 *
 * Required props:
 *   surface      — registered surface key ("dashboard", etc.). Must match
 *                  routers/splash_admin.py SURFACES.
 *   ctaId        — feature id within that surface (must match the registry).
 *   defaultDescription / defaultCtaLabel — A-variant baseline displayed when
 *                  no DB override exists.
 *   variantB     — optional inline `{description, cta_label}` for the B-variant
 *                  baseline (DB override always wins). When omitted, a feature
 *                  is single-variant (A only) until an admin saves a B.
 *   onCtaClick   — fires after the splash_cta-equivalent event is recorded.
 *
 * Optional props: title, icon, className, dismissible, eventNames (override
 * the default event tuple — useful for legacy surfaces).
 */

const SEED_KEY = 'safer_cta_ab_seed';

function hashStr(input) {
  let h = 0x811c9dc5;
  const s = String(input);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

function pickVariant(seedKey, hasB) {
  if (!hasB) return 'a';
  let seed = '';
  try {
    const raw = sessionStorage.getItem('safer_user') || localStorage.getItem('safer_user');
    if (raw) {
      const u = JSON.parse(raw);
      seed = u?.user_id || u?.email || '';
    }
    if (!seed) {
      seed = localStorage.getItem(SEED_KEY) || '';
      if (!seed) {
        seed = `dev-${Math.random().toString(36).slice(2)}-${Date.now()}`;
        localStorage.setItem(SEED_KEY, seed);
      }
    }
  } catch {
    seed = 'fallback';
  }
  return hashStr(`${seed}|${seedKey}`) % 2 === 0 ? 'a' : 'b';
}

// Surfaces that need to fire telemetry from a pre-auth page must use the
// public ingestion endpoint instead of /audit/telemetry/event (which 401s
// without a JWT). Keep this list in sync with PUBLIC_CTA_SURFACE_ALLOWLIST
// in backend/routers/usage_audit.py.
const PUBLIC_TELEMETRY_SURFACES = new Set(['signup_recovery']);

function emitPublicCtaEvent(type, key, value, surface) {
  try {
    const session_id = getSignupFunnelSessionId();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/telemetry/public-cta-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, key, value: value || null, surface, session_id }),
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
}

// In-memory cache so a page with multiple TrackedCTAs only fetches once.
const _activeCache = new Map(); // surface -> Promise<{a_overrides, b_overrides}>
function getActive(surface) {
  if (!_activeCache.has(surface)) {
    const isPublic = PUBLIC_TELEMETRY_SURFACES.has(surface);
    // For pre-auth surfaces use a plain fetch (api.silentRequest 401-bounces
    // when no JWT is present, even with skipAuthRedirect). For authed
    // surfaces keep the existing path so request interceptors are honoured.
    const fetchPromise = isPublic
      ? fetch(`${process.env.REACT_APP_BACKEND_URL}/api/splash/cards/active?surface=${encodeURIComponent(surface)}`)
          .then((r) => r.ok ? r.json() : { a_overrides: {}, b_overrides: {} })
          .then((data) => ({ a: data?.a_overrides || {}, b: data?.b_overrides || {} }))
          .catch(() => ({ a: {}, b: {} }))
      : api.silentRequest({ method: 'GET', url: `/splash/cards/active?surface=${encodeURIComponent(surface)}` })
          .then((res) => ({
            a: res?.data?.a_overrides || {},
            b: res?.data?.b_overrides || {},
          }))
          .catch(() => ({ a: {}, b: {} }));
    _activeCache.set(surface, fetchPromise);
  }
  return _activeCache.get(surface);
}

export default function TrackedCTA({
  surface,
  ctaId,
  defaultDescription,
  defaultCtaLabel,
  variantB = null,
  title = null,
  icon: Icon = null,
  onCtaClick,
  className = '',
  eventNames = ['cta_view', 'cta_click', 'cta_dismiss'],
  testIdPrefix = 'tracked-cta',
}) {
  const [overrides, setOverrides] = useState({ a: null, b: null });
  const [loaded, setLoaded] = useState(false);
  const seenRef = useRef(false);

  const hasB = !!(variantB || overrides.b);
  const variant = useMemo(
    () => pickVariant(`${surface}|${ctaId}`, hasB),
    // re-derive when B becomes/disappears (override fetch arrives)
    [surface, ctaId, hasB],
  );

  // Resolve display copy: DB override > inline default > prop default.
  const description =
    variant === 'a'
      ? overrides.a?.description ?? defaultDescription
      : (overrides.b?.description ?? variantB?.description ?? defaultDescription);
  const ctaLabel =
    variant === 'a'
      ? overrides.a?.cta_label ?? defaultCtaLabel
      : (overrides.b?.cta_label ?? variantB?.cta_label ?? defaultCtaLabel);

  // Fetch overrides once per surface
  useEffect(() => {
    let cancelled = false;
    getActive(surface).then((m) => {
      if (cancelled) return;
      setOverrides({ a: m.a[ctaId] || null, b: m.b[ctaId] || null });
      setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [surface, ctaId]);

  // Emit a single view event after the override fetch resolves so the variant
  // tag matches what the user actually sees.
  useEffect(() => {
    if (!loaded || seenRef.current) return;
    seenRef.current = true;
    if (PUBLIC_TELEMETRY_SURFACES.has(surface)) {
      emitPublicCtaEvent(eventNames[0], ctaId, variant, surface);
    } else {
      trackEvent(eventNames[0], ctaId, variant);
    }
  }, [loaded, ctaId, variant, eventNames, surface]);

  const handleClick = (e) => {
    if (PUBLIC_TELEMETRY_SURFACES.has(surface)) {
      emitPublicCtaEvent(eventNames[1], ctaId, variant, surface);
    } else {
      trackEvent(eventNames[1], ctaId, variant);
    }
    if (onCtaClick) onCtaClick(e);
  };

  return (
    <div className={`rounded-xl border bg-gradient-to-br from-violet-50 via-white to-indigo-50 px-5 py-5 ${className}`} data-testid={`${testIdPrefix}-${ctaId}`}>
      <div className="flex items-start gap-3">
        {Icon ? (
          <div className="rounded-lg bg-white border border-violet-200 p-2 mt-0.5">
            <Icon className="w-4 h-4 text-violet-600" />
          </div>
        ) : null}
        <div className="flex-1 min-w-0">
          {title ? (
            <p className="text-sm font-semibold text-slate-900">{title}</p>
          ) : null}
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">{description}</p>
          <div className="mt-3">
            <Button
              type="button"
              size="sm"
              onClick={handleClick}
              data-testid={`${testIdPrefix}-${ctaId}-btn`}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {ctaLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
